import { gql, useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useApplyOverrides } from "../../../commons/hooks/useApplyOverrides.js";
import {
  BreadcrumbItem,
  Page,
  PageTopbarItem,
} from "../../../commons/layout/Page.js";
import { PageContainer } from "../../../commons/layout/PageContainer.js";
import { TemplatesQuoteTemplateAction } from "../../../features/templates/quote/TemplatesQuoteTemplateAction.js";
import { ThreeD } from "../../../features/threeD/ThreeD.js";
import { ThreeDEditorRef } from "../../../features/threeD/ThreeDEditor.js";
import { fromVersionNumberParamToVersionNumberInt } from "../../../utils.js";
import { useTemplateQuoteThreeDVersionQuery } from "./TemplateQuoteVersionThreeD.generated.js";

interface Props {
  docId: string;
  pathToDoc: string;
  pathToDocList: string;
  pathToDocVersion: string;
  submenuItems: PageTopbarItem[];
  prefixBreadcrumbs: BreadcrumbItem[];
  returnPath?: string | null;
  pageSubHeader: React.ReactElement | null;
  hasOverridesDefined: boolean;
}

export const TemplateQuoteThreeDVersion = ({
  docId,
  pathToDoc,
  pathToDocList,
  pathToDocVersion,
  submenuItems,
  prefixBreadcrumbs,
  pageSubHeader,
  hasOverridesDefined,
}: Props) => {
  const { t } = useTranslate(["Templates"]);
  const [applyOverrides] = useApplyOverrides();

  const { versionNumber: versionNumberFromParam } = useParams();
  const versionNumber = fromVersionNumberParamToVersionNumberInt(
    versionNumberFromParam
  );
  if (!versionNumber) throw new Error("Version number missing");
  const [searchParams] = useSearchParams();
  const returnPath = searchParams.get("r");

  const client = useApolloClient();
  const query = useTemplateQuoteThreeDVersionQuery({
    client,
    variables: {
      id: docId,
      versionNumber,
      applyOverrides: hasOverridesDefined && applyOverrides,
    },
    ...(hasOverridesDefined && applyOverrides
      ? {
          fetchPolicy: "no-cache",
          nextFetchPolicy: "no-cache",
        }
      : undefined),
  });

  const templateVersion = getDataOrNull(
    query.data?.quoteTemplateVersion
  )?.quoteTemplate;
  const allDocItems = React.useMemo(() => {
    if (!templateVersion) return [];

    return templateVersion.items;
  }, [templateVersion]);

  const threeDEditorRef = React.useRef<ThreeDEditorRef>(null);

  return (
    <Page
      title={templateVersion?.title ?? ""}
      subtitle={t("Document Templates", { ns: "Templates" })}
      submenuItems={submenuItems}
      breadcrumbs={prefixBreadcrumbs}
      subHeader={pageSubHeader}
      headerType="template"
      action={
        templateVersion ? (
          <TemplatesQuoteTemplateAction
            doc={templateVersion}
            pathToDoc={pathToDoc}
            pathToDocList={pathToDocList}
          />
        ) : undefined
      }
    >
      {templateVersion && (
        <PageContainer $fullHeight $noPadding>
          <ThreeD
            threeDEditorRef={threeDEditorRef}
            projectId={null}
            docId={docId}
            returnPath={returnPath ?? pathToDocVersion}
            allDocItems={allDocItems}
            isEditable={false}
          />
        </PageContainer>
      )}
    </Page>
  );
};
